import { useCallback, type ReactNode } from "react";
import {
  DataGridView,
  type DataGridViewProps,
  type DataTableColumn,
  type DataTableColumnVisibility,
  type DataTableSource,
  type TableRow,
} from "@carbon6/c6-frontend-design";
import { ListRefreshProvider } from "@/components/refresh";
import { HatDataTable } from "@/components/table/HatDataTable";
import { useTableStateMessages } from "@/components/table/tableStateMessages";
import type { DatasetViewMode } from "./DatasetViewModeToggle";

// 카드 그리드 셀 폭. 한 줄에 2열이다(셀당 약 1/2, gap 1개 보정).
// 폭이 좁아지면 셀은 floor 값(20rem)까지 줄어든다. 그보다 좁으면 1열이 된다.
export const datasetResourceCardCellWidth =
  "clamp(20rem, calc((100% - var(--app-spacing-large)) / 2), 100%)";

export type DatasetResourceViewProps<Row extends TableRow> = {
  actionsColumnWidth?: string;
  ariaLabel: string;
  cellWidth?: string;
  columnVisibility?: DataTableColumnVisibility;
  columns: readonly DataTableColumn<Row>[];
  emptyMessage: ReactNode;
  loadingMessage: ReactNode;
  minWidth: string;
  renderCard: (row: Row) => ReactNode;
  source: DataTableSource<Row>;
  viewMode: DatasetViewMode;
  // true 이면 표/그리드에 선택 체크박스를 표시한다. 선택 상태는 source 가 소유한다.
  showCheckbox?: boolean;
};

export function DatasetResourceView<Row extends TableRow>({
  actionsColumnWidth,
  ariaLabel,
  // 기본값은 전폭 단일 컬럼이다. 페이지별 카드 폭 정책은 cellWidth로 명시한다.
  cellWidth = "auto",
  columnVisibility,
  columns,
  emptyMessage,
  loadingMessage,
  minWidth,
  renderCard,
  source,
  viewMode,
  showCheckbox = false,
}: DatasetResourceViewProps<Row>): ReactNode {
  // 셀 안의 깊은 컴포넌트(메모 팝업, 카드 등)가 prop 전달 없이 목록을 다시 조회하는 seam.
  // source 는 페이지가 소유해 바뀌지 않으므로 identity 를 고정한다.
  const refreshList = useCallback(() => void source.refresh(), [source]);

  if (viewMode === "table") {
    return (
      <ListRefreshProvider refresh={refreshList}>
        <HatDataTable
          source={source}
          columns={columns}
          columnVisibility={columnVisibility}
          emptyMessage={emptyMessage}
          loadingMessage={loadingMessage}
          ariaLabel={`${ariaLabel} table`}
          scrollable
          actionsColumnWidth={actionsColumnWidth}
          minWidth={minWidth}
          showCheckbox={showCheckbox}
          // c6 DataTable 은 기본으로 행 번호(#)를 그린다. 이 목록은 행 번호로 행을 식별하지 않으므로 끈다.
          showIndex={false}
        />
      </ListRefreshProvider>
    );
  }

  return (
    <ListRefreshProvider refresh={refreshList}>
      <ResourceGrid
        source={source}
        cellWidth={cellWidth}
        cellHeight="auto"
        emptyMessage={emptyMessage}
        loadingMessage={loadingMessage}
        ariaLabel={`${ariaLabel} grid`}
        scrollable
        renderItem={renderCard}
        showCheckbox={showCheckbox}
        // 카드 뷰는 카드별 체크박스만 표시한다. 전체 선택 줄은 표 헤더에만 둔다.
        showSelectAll={false}
      />
    </ListRefreshProvider>
  );
}

// 카드 격자. 빈·대기·실패 자리는 표(`HatDataTable`)와 같은 형태를 쓴다. 그래서 보기를 바꿔도 같은 자리다.
function ResourceGrid<Row extends TableRow>({
  emptyMessage,
  loadingMessage,
  ...props
}: Omit<DataGridViewProps<Row>, "errorMessage">): ReactNode {
  const stateMessages = useTableStateMessages(props.source, { emptyMessage, loadingMessage });
  return <DataGridView {...props} {...stateMessages} />;
}
